/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useVendorVerification } from '../../hooks/useVendorVerification';
import useVendorPlanAccess from '../../hooks/useVendorPlanAccess';
import VendorVerificationModal from '../vendor/VendorVerificationModal';
import { useUnreadMessagesCount } from '../../services/messageService';
import { useUnreadNotificationsCount, useUnreadSupportTicketCount } from '../../services/notificationService';
import { 
  LayoutDashboard, 
  User, 
  Calendar, 
  Image, 
  BarChart3, 
  Settings,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CreditCard,
  DollarSign,
  Star,
  Package,
  MapPin,
  Clock,
  CheckCircle,
  Crown,
  Eye,
  Download,
  Upload,
  HelpCircle,
  Zap,
  Target,
  BookOpen,
  FileCheck,
  Bell,
  Shield,
  TrendingUp,
  Lock,
  Camera,
  Video,
  Gift,
  Heart,
  Users,
  UserCheck,
  FileText,
  FileSignature,
  Wallet,
  Info,
  Share,
  LogOut,
  Palette,
  Headphones,
  Building2 as Building,
} from 'lucide-react';

/** Match nav href to pathname without false positives (e.g. /vendor/support vs /vendor/support-tickets). */
function navPathMatches(href, pathname) {
  if (!href || !pathname) return false;
  if (href === '/vendor') {
    return pathname === '/vendor' || pathname === '/vendor/';
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

const VendorSidebar = () => {
  const location = useLocation();
  const { 
    isVendorSidebarCollapsed, 
    toggleVendorSidebar, 
    isVendorMobileSidebarOpen, 
    isVendorMobileSidebarCollapsed,
    toggleVendorMobileSidebar,
    closeVendorMobileSidebar 
  } = useSidebar();
  const { user, logout } = useAuth();
  const { canAccessPath, isFreeTier } = useVendorPlanAccess();
  const { 
    isVendorVerified, 
    isVerificationPending,
    showVerificationModal, 
    showVerificationRequired, 
    hideVerificationModal,
    isLoading,
    verificationStatus
  } = useVendorVerification();

  // Get unread counts for badges
  const { data: unreadMessagesCount = 0 } = useUnreadMessagesCount();

  const { data: unreadNotificationsData } = useUnreadNotificationsCount();
  const unreadNotificationsCount = unreadNotificationsData?.data ?? unreadNotificationsData ?? 0;

  const { data: unreadSupportTicketsData } = useUnreadSupportTicketCount();
  const unreadSupportTicketsCount = unreadSupportTicketsData ?? 0;

  const [openDropdowns, setOpenDropdowns] = useState({
    Business: true,
    Services: true,
    Bookings: true,
  });

  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdownName]: !prev[dropdownName]
    }));
  };

  const handleLogout = async () => {
    await logout();
  };

  /**
   * Sidebar order mirrors a vendor’s real workflow:
   * Home → Business setup → Offerings → Jobs → Showcase → Money → Insights → Plan → Inbox → Settings
   */
  const navigationGroups = [
    {
      type: 'single',
      items: [
        { name: 'Dashboard', href: '/vendor', icon: LayoutDashboard, requiresVerification: false },
      ],
    },
    {
      type: 'dropdown',
      name: 'Business',
      icon: Building,
      items: [
        { name: 'Account', href: '/vendor/account', icon: User, requiresVerification: false },
        { name: 'Profile preview', href: '/vendor/profile', icon: Eye, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Services',
      icon: Package,
      items: [
        { name: 'Services', href: '/vendor/services', icon: Package, requiresVerification: true },
        { name: 'Packages', href: '/vendor/packages', icon: Gift, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Bookings',
      icon: Calendar,
      items: [
        { name: 'All bookings', href: '/vendor/bookings', icon: Calendar, requiresVerification: true },
        { name: 'Private bookings', href: '/vendor/private-bookings', icon: Lock, requiresVerification: true },
        { name: 'Consultations', href: '/vendor/consultations', icon: Video, requiresVerification: true },
        { name: 'Calendar', href: '/vendor/calendar', icon: Clock, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Showcase',
      icon: Image,
      items: [
        { name: 'Portfolio', href: '/vendor/portfolio', icon: Image, requiresVerification: true },
        { name: 'Photo gallery', href: '/vendor/gallery', icon: Camera, requiresVerification: true },
        { name: 'Custom branding', href: '/vendor/branding', icon: Palette, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Money',
      icon: DollarSign,
      items: [
        { name: 'Accounting', href: '/vendor/accounting', icon: BookOpen, requiresVerification: true },
        { name: 'Invoices', href: '/vendor/invoices', icon: FileText, requiresVerification: true },
        { name: 'Contracts', href: '/vendor/contracts', icon: FileSignature, requiresVerification: true },
        { name: 'Earnings', href: '/vendor/earnings', icon: DollarSign, requiresVerification: true },
        { name: 'Payments', href: '/vendor/payments', icon: CreditCard, requiresVerification: true },
        { name: 'Discount codes', href: '/vendor/discount-codes', icon: Gift, requiresVerification: true },
        { name: 'Payouts', href: '/vendor/payout-disbursements', icon: Wallet, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Insights',
      icon: BarChart3,
      items: [
        { name: 'Analytics', href: '/vendor/analytics', icon: BarChart3, requiresVerification: true },
        { name: 'Advanced analytics', href: '/vendor/advanced-analytics', icon: TrendingUp, requiresVerification: true },
        { name: 'Performance', href: '/vendor/performance', icon: Target, requiresVerification: true },
        { name: 'Reports', href: '/vendor/reports', icon: FileCheck, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'My Plan',
      icon: Crown,
      items: [
        { name: 'Plans & pricing', href: '/vendor/subscription', icon: CreditCard, requiresVerification: false },
        { name: 'Priority support', href: '/vendor/support-tickets', icon: Headphones, requiresVerification: true },
        { name: 'Advanced booking', href: '/vendor/advanced-bookings', icon: Calendar, requiresVerification: true },
        { name: 'Multi-location', href: '/vendor/locations', icon: MapPin, requiresVerification: true },
      ],
    },
    {
      type: 'dropdown',
      name: 'Inbox',
      icon: Bell,
      items: [
        { name: 'Notifications', href: '/vendor/notifications', icon: Bell, requiresVerification: true },
        { name: 'Reviews', href: '/vendor/reviews', icon: Star, requiresVerification: true },
        { name: 'Support', href: '/vendor/support', icon: HelpCircle, requiresVerification: false },
      ],
    },
    {
      type: 'dropdown',
      name: 'Settings',
      icon: Settings,
      items: [
        { name: 'Subscription', href: '/vendor/my-subscription', icon: Crown, requiresVerification: false },
        { name: 'Security', href: '/vendor/security', icon: Shield, requiresVerification: false },
        { name: 'Preferences', href: '/vendor/preferences', icon: Settings, requiresVerification: false },
      ],
    },
  ];
  // Show all navigation groups (filter items according to plan and upgrade status)
  const getNavigationGroups = () => {
    return navigationGroups
      .map((group) => {
        // Hide "Plans & pricing" from sidebar once vendor has upgraded to a paid subscription
        let items = group.items;
        if (group.name === 'My Plan' && !isFreeTier) {
          items = items.filter((item) => item.href !== '/vendor/subscription');
        }

        if (group.type === 'single') {
          return {
            ...group,
            items: items.filter((item) => canAccessPath(item.href)),
          };
        }
        if (group.type === 'dropdown') {
          return {
            ...group,
            items: items.filter((item) => canAccessPath(item.href)),
          };
        }
        return group;
      })
      .filter((group) => (group.items?.length ?? 0) > 0);
  };

  // Keep the section that owns the current route open (reload + in-app navigation)
  useEffect(() => {
    const pathname = location.pathname;
    setOpenDropdowns((prev) => {
      const next = { ...prev };
      let changed = false;
      navigationGroups.forEach((group) => {
        if (group.type !== 'dropdown') return;
        const hasActiveChild = group.items.some((item) =>
          navPathMatches(item.href, pathname)
        );
        if (hasActiveChild && !next[group.name]) {
          next[group.name] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle navigation click
  const handleNavigationClick = (item, e) => {
    // console.log('VendorSidebar: Navigation click:', {
    //   itemName: item.name,
    //   requiresVerification: item.requiresVerification,
    //   isVendorVerified,
    //   isVerificationPending,
    //   showVerificationModal
    // });
    
    if (item.requiresVerification && !isVendorVerified) {
      e.preventDefault();
      console.log('VendorSidebar: Showing verification modal');
      showVerificationRequired();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isVendorMobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[10990] lg:hidden"
          onClick={closeVendorMobileSidebar}
        />
      )}
      
      {/* Sidebar */}
      <div
        className={`lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:pt-16 lg:z-40 transition-all duration-300 ${
          isVendorSidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        } ${
          isVendorMobileSidebarOpen
            ? `fixed inset-y-0 left-0 z-[10995] pt-16 flex flex-col ${
                isVendorMobileSidebarCollapsed ? 'w-16' : 'w-64'
              }`
            : 'hidden'
        }`}
      >
      <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-[#111827] border-r border-gray-200 dark:border-gray-800/90 transition-colors">
        {/* Profile Section */}
        <div className="px-4 py-5 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center shadow-sm">
                <span className="text-white font-semibold text-sm">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'V'}
                </span>
              </div>
            </div>
            {!isVendorSidebarCollapsed && !isVendorMobileSidebarCollapsed && (
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {user?.name || 'Vendor'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.email || ''}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                    Active
                  </span>
                  {isFreeTier && (
                    <>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-100">
                        Free plan
                      </span>
                      <Link
                        to="/vendor/subscription"
                        className="text-[10px] font-semibold text-primary-600 hover:text-primary-800"
                        onClick={() => closeVendorMobileSidebar()}
                      >
                        Upgrade
                      </Link>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Collapse Toggle */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => {
              // Use mobile toggle on mobile, desktop toggle on desktop
              if (window.innerWidth < 1024) {
                toggleVendorMobileSidebar();
              } else {
                toggleVendorSidebar();
              }
            }}
            className="w-full flex items-center justify-center p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            {(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto scrollbar-thin">
          {getNavigationGroups().map((group, groupIndex) => {
            if (group.type === 'single') {
              return group.items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={(e) => handleNavigationClick(item, e)}
                  className={({ isActive }) =>
                    `group flex items-center px-2.5 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                    } ${
                      item.requiresVerification && !isVendorVerified 
                        ? isVerificationPending 
                          ? 'opacity-75 cursor-default' 
                          : 'opacity-60 cursor-not-allowed'
                        : ''
                    }`
                  }
                  title={(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? item.name : undefined}
                >
                  <item.icon className={`h-5 w-5 shrink-0 ${(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? '' : 'mr-3'}`} />
                  {!(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) && item.name}
                </NavLink>
              ));
            }

            if (group.type === 'dropdown') {
              const hasActiveChild = group.items.some((item) =>
                navPathMatches(item.href, location.pathname)
              );
              // Active section always stays expanded so reload / deep links keep context
              const isDropdownOpen = hasActiveChild || !!openDropdowns[group.name];
              const isCollapsed = isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed;
              
              return (
                <div key={group.name} className="space-y-0.5">
                  {/* Section label when previous was different type */}
                  {groupIndex > 0 && !isCollapsed && (
                    <div className="pt-2 pb-1 px-2.5 first:pt-0" aria-hidden />
                  )}
                  {/* Dropdown Header */}
                  <button
                    type="button"
                    onClick={() => {
                      if (hasActiveChild) return;
                      toggleDropdown(group.name);
                    }}
                    className={`group w-full flex items-center px-2.5 py-2 text-sm font-semibold rounded-lg transition-colors ${
                      isDropdownOpen || hasActiveChild
                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                    }`}
                    title={isCollapsed ? group.name : undefined}
                    aria-expanded={isDropdownOpen}
                  >
                    <group.icon className={`h-5 w-5 shrink-0 ${isCollapsed ? '' : 'mr-3'}`} />
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-left tracking-tight">{group.name}</span>
                        <ChevronDown 
                          className={`h-4 w-4 text-gray-400 transition-transform ${
                            isDropdownOpen ? 'rotate-180' : ''
                          }`} 
                        />
                      </>
                    )}
                  </button>
                  
                  {/* Dropdown Items */}
                  {!isCollapsed && isDropdownOpen && (
                    <div className="ml-3 pl-2 border-l border-gray-200 dark:border-gray-700 space-y-0.5">
                      {group.items.map((item) => {
                        // Get badge count for Messages, Notifications, and Priority Support
                        const badgeCount = item.name === 'Messages' 
                          ? unreadMessagesCount 
                          : item.name === 'Notifications' 
                          ? unreadNotificationsCount 
                          : (item.name === 'Priority support' || item.name === 'Priority Support')
                          ? unreadSupportTicketsCount
                          : 0;

                        return (
                          <NavLink
                            key={item.name}
                            to={item.href}
                            onClick={(e) => handleNavigationClick(item, e)}
                            className={({ isActive }) =>
                              `group flex items-center justify-between px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                isActive
                                  ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                              } ${
                                item.requiresVerification && !isVendorVerified 
                                  ? isVerificationPending 
                                    ? 'opacity-75 cursor-default' 
                                    : 'opacity-60 cursor-not-allowed'
                                  : ''
                              }`
                            }
                          >
                            <div className="flex items-center min-w-0">
                              <item.icon className="h-4 w-4 mr-2.5 shrink-0" />
                              <span className="truncate">{item.name}</span>
                            </div>
                            {badgeCount > 0 && (
                              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold bg-primary-600 text-white rounded-full min-w-[1.15rem] text-center">
                                {badgeCount > 99 ? '99+' : badgeCount}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            return null;
          })}
        </nav>

        {/* Logout Section */}
        <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-2 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50 rounded-md transition-colors group"
            title={(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? 'Logout' : undefined}
          >
            <LogOut className={`h-5 w-5 ${(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) ? '' : 'mr-3'}`} />
            {!(isVendorSidebarCollapsed || isVendorMobileSidebarCollapsed) && 'Logout'}
          </button>
        </div>
      </div>
      </div>
      
      {/* Verification Modal */}
      <VendorVerificationModal
        isOpen={showVerificationModal}
        onClose={hideVerificationModal}
        isPending={isVerificationPending}
      />
    </>
  );
};

export default VendorSidebar;
